import { useEffect, useRef, useState } from 'react'

import { DIALOG_IDS, modalStore } from '@core/modal'
import { PlanType } from '@core/models'
import { readHqStoragePaywallSeen } from '@core/session-dialogs'
import { useActions } from '@core/store'
import { useCurrentUser } from '@features/session'
import { usePlan } from '@features/subscription'

import { type TLosslessFormat } from '../helpers/getLosslessFormat'
import {
    startHqStoragePaywallFlow,
    type THqStoragePaywallFlow,
} from '../helpers/hqStoragePaywallFlow'
import { hqStoragePaywallStore } from '../store/hqStoragePaywallStore'

type TPendingRequest = {
    fileFormat: TLosslessFormat
    flow: THqStoragePaywallFlow
}

export const useHqStoragePaywall = ({
    isFirstUpload,
}: {
    isFirstUpload: (browsableAudioCount: number) => boolean
}) => {
    const { browsableAudioCount } = useCurrentUser()
    const { data: plan, isPending: isPlanPending } = usePlan()
    const actions = useActions({
        setFileFormat: hqStoragePaywallStore.actions.setFileFormat,
        openModal: modalStore.actions.open,
    })
    const [pending, setPending] = useState<TPendingRequest | null>(null)

    const show = ({ fileFormat, flow }: TPendingRequest) => {
        startHqStoragePaywallFlow(flow)
        actions.setFileFormat(fileFormat)
        actions.openModal(DIALOG_IDS.hqStoragePaywall)
    }

    const showRef = useRef(show)

    showRef.current = show

    const showWhenReady = (fileFormat: TLosslessFormat, flow: THqStoragePaywallFlow = {}) => {
        if (!isFirstUpload(browsableAudioCount)) {
            return
        }

        setPending({ fileFormat, flow })
    }

    const planType = plan?.type

    useEffect(() => {
        if (!pending || isPlanPending) {
            return
        }

        if (planType !== PlanType.Free) {
            setPending(null)

            return
        }

        setPending(null)

        if (readHqStoragePaywallSeen()) {
            return
        }

        showRef.current(pending)
    }, [pending, isPlanPending, planType])

    return { showWhenReady }
}
